import React, { useState, useRef, useEffect } from "react";

/**
 * ExportMenu — small dropdown emitting CSV / XLSX download links.
 *
 * Props:
 *   items: [{ label, url }] — each entry becomes "<label> (CSV)" / "(Excel)"
 *          if it provides both csvUrl + xlsxUrl, OR is rendered as a single
 *          link when only one is provided.
 *
 * Cookies travel with same-origin GET, so a plain anchor href is enough.
 */
export default function ExportMenu({ items, label = "Export", testId = "export-menu" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="border border-zinc-300 px-3 py-2 text-xs font-mono rounded hover:bg-zinc-50"
        data-testid={`${testId}-trigger`}>
        {label} ▾
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-64 rounded border border-zinc-200 bg-white shadow-lg py-1"
             data-testid={`${testId}-panel`}>
          {items.map((item) => (
            <div key={item.label} className="px-3 py-1.5">
              <div className="text-[10px] uppercase font-mono text-zinc-500 mb-0.5">{item.label}</div>
              <div className="flex gap-2">
                {item.csvUrl && (
                  <a href={item.csvUrl}
                     className="text-xs font-mono text-emerald-700 hover:text-emerald-900 underline"
                     data-testid={`${testId}-${item.label.toLowerCase().replace(/\s+/g, "-")}-csv`}
                     onClick={() => setOpen(false)}>
                    CSV
                  </a>
                )}
                {item.xlsxUrl && (
                  <a href={item.xlsxUrl}
                     className="text-xs font-mono text-emerald-700 hover:text-emerald-900 underline"
                     data-testid={`${testId}-${item.label.toLowerCase().replace(/\s+/g, "-")}-xlsx`}
                     onClick={() => setOpen(false)}>
                    Excel
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
